import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, FormHelperText } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Documents, EditPage, FuseInput, FuseSelect, LoanSelect, Notes } from '/src/components';
import { useAssetStore, useFilterStore, useUserStore } from '/src/context';
import { formInputDate } from '/src/lib/constants';
import { toastMessages } from '/src/lib/messages';
import { Asset, Note } from '/src/lib/models';
import { AssetStatus, AssetType, CRUDMode, DataModelType, FormErrors } from '/src/lib/types';
import { isNumber } from '/src/utils';

/**
 * Edit Asset Page
 */
export const EditAssetPage = observer(() => {
  /** Context **/
  const assetStore = useAssetStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<Asset>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState([
    false, // Company Comments
    false, // Socket Comments
    false, // Documents
  ]);

  /** Computed **/
  const loans = useMemo(() => filterStore.loanListGet(), [filterStore._loanList]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const errorMessages = {
      effectiveDate: 'Effective date is required',
      name: 'Asset name is required',
      program: 'Program is required',
      paymentSchedule: 'Payment schedule is required',
      nextDate: 'Next payment date is required',
      lateFee: 'Late fee is required for owned loans',
    };

    if (!formValues?.effectiveDate) errors.effectiveDate = errorMessages['effectiveDate'];
    if (!formValues?.name) errors.name = errorMessages['name'];
    if (!formValues?.program) errors.program = errorMessages['program'];
    if (!formValues?.paymentSchedule) errors.paymentSchedule = errorMessages['paymentSchedule'];
    if (!formValues?.nextDate) errors.nextDate = errorMessages['nextDate'];
    if (formValues?.loan?.isOwned && !isNumber(formValues?.lateFee)) errors.lateFee = errorMessages['lateFee'];

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the Asset.
   *
   * @remarks
   * Redirects to the Asset's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    // Validate form before submitting
    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await assetStore.update(formValues);
      if (!response) throw new Error(`Could not update Asset with ID "${formValues._id}"`);

      toast.success(toastMessages.updateAsset.success);
      navigate(`/assets/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updateAsset.error);
    }
  };

  /**
   * Updates the Company Comments list.
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateCompanyComments = async (companyComments: Note[]) => {
    if (!formValues) return;

    const response = await assetStore.update({ ...formValues, companyComments });
    if (!response) throw new Error(`Error updating company comments for Asset ID ${formValues._id}`);

    assetStore.currentAsset = await assetStore.get(formValues._id);
  };

  /**
   * Update Socket Comments
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateSocketComments = async (socketComments: Note[]) => {
    if (!formValues) return;

    const response = await assetStore.update({ ...formValues, socketComments });
    if (!response) throw new Error(`Error updating socket comments for Asset ID ${formValues._id}`);

    assetStore.currentAsset = await assetStore.get(formValues._id);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!formValues) return;

    await assetStore.update({ ...formValues, files });

    updateFormValue('files')(files);
  };

  /** Effects **/

  useEffect(() => {
    if (!params.assetId) return;

    Promise.all([assetStore.get(params.assetId)]);
  }, [params.assetId]);

  useEffect(() => {
    if (assetStore.currentAsset) {
      setFormValues(assetStore.currentAsset);
    }
  }, [assetStore.currentAsset]);

  /** Render **/
  const renderCompanyComments = (index = 0) => (
    <Notes
      mode={CRUDMode.Edit}
      notes={formValues?.companyComments ?? []}
      title="Company Comments"
      tooltip="Add Company Comment"
      sidebarIndex={index}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      updateNotes={updateCompanyComments}
    />
  );

  const renderSocketComments = (index = 1) => (
    <Notes
      mode={CRUDMode.Edit}
      notes={formValues?.socketComments ?? []}
      title="Socket Comments"
      tooltip="Add Socket Comment"
      sidebarIndex={index}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      updateNotes={updateSocketComments}
    />
  );

  const renderDocuments = (index = 2) => (
    <Documents
      mode={CRUDMode.Edit}
      documents={formValues?.files ?? []}
      sidebarIndex={index}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      updateDocuments={updateFiles}
    />
  );

  return (
    <EditPage
      modelName={assetStore.currentAsset?.name ?? ''}
      modelType={DataModelType.Asset}
      editVerb="Edit"
      onEdit={update}
      sidebarFlex="30"
      contentFlex="70"
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      sidebar={
        <>
          {renderCompanyComments()}
          {userStore.isAdmin && renderSocketComments()}
          {renderDocuments()}
        </>
      }
    >
      {/* Loan */}
      <LoanSelect
        id="loan"
        value={formValues?.loan?._id ?? ''}
        setValue={updateFormValue('loan')}
        loans={loans}
        disabled
        error={formErrors.loan}
      />

      {/* Asset Name */}
      <FuseInput
        id="name"
        label="Asset Name"
        value={formValues?.name}
        updateValue={updateFormValue('name')}
        required
        error={formErrors.name}
      />

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Asset Type */}
        <FuseSelect
          id="assetType"
          label="Asset Type"
          options={Object.entries(AssetType).map(([label, value]) => ({ label, value }))}
          value={formValues?.assetType}
          updateValue={updateFormValue('assetType')}
          placeholder="Asset Type"
          flex="33"
          required
          error={formErrors.assetType}
        />

        {/* Status */}
        <FuseSelect
          id="status"
          label="Status"
          options={Object.entries(AssetStatus).map(([label, value]) => ({ label, value }))}
          value={formValues?.status}
          updateValue={updateFormValue('status')}
          placeholder="Asset Status"
          ariaLabel="Select an Asset Status"
          required
          error={formErrors.assetType}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        <Box display="flex" flexDirection="column" flex={50}>
          <Box className="text-label">
            Value -{' '}
            <strong>
              <Link to="/adjustments/create">Add an Adjustment</Link> to change asset value.
            </strong>
          </Box>
          <FuseInput
            id="value"
            label=""
            aria-label="Asset Value"
            value={formValues?.value}
            updateValue={updateFormValue('value')}
            type="number"
            moneyMask
            required
            disabled
            disableMargin
            tooltip="Add an Adjustment to change value"
          />
          {formErrors?.value && <FormHelperText>{formErrors?.value}</FormHelperText>}
        </Box>

        <Box display="flex" flex={50}>
          <FuseInput
            id="program"
            label="Program"
            value={formValues?.program}
            updateValue={updateFormValue('program')}
            required
            error={formErrors.program}
          />
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        <FuseInput
          id="paymentSchedule"
          label="Payment Schedule"
          value={formValues?.paymentSchedule}
          updateValue={updateFormValue('paymentSchedule')}
          required
          error={formErrors.paymentSchedule}
        />
        <FuseInput
          id="nextDate"
          label="Next Payment"
          value={dayjs(formValues?.nextDate).format(formInputDate)}
          updateValue={updateFormValue('nextDate')}
          type="date"
          required
          error={formErrors.nextDate}
        />
      </Box>

      {formValues?.loan.isOwned && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <FuseInput
            id="lateFee"
            label="Late Fee"
            value={formValues?.lateFee}
            updateValue={updateFormValue('lateFee')}
            type="number"
            moneyMask
            required={formValues.loan.isOwned}
            error={formErrors.lateFee}
          />
        </Box>
      )}

      <FuseInput
        id="effectiveDate"
        label="Asset Effective Date"
        value={formValues?.effectiveDate}
        updateValue={updateFormValue('effectiveDate')}
        type="date"
        required
        error={formErrors.effectiveDate}
        boxProps={{ paddingBottom: '6px' }}
      />
    </EditPage>
  );
});
